import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  DisplayUser,
  GalleryDto,
  GalleryPublicAccessLevel,
  GalleryTenantAccessLevel,
  TenantDto,
} from '@nusign/picflow-backend-types';
import { Observable, catchError, first, firstValueFrom, forkJoin, lastValueFrom, map, of, take } from 'rxjs';
import { billingScopes } from '../billing-consts';
import { accessLevelOrder } from '../consts';
import { GalleryAccessLevel } from '../lib/access-level';
import { forkJoinSafe } from '../lib/safe-fork-join';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { GalleryMemberService } from './gallery-member.service';
import { GalleryPasswordService } from './gallery-password.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { PermissionService } from './permission.service';
import { StateService } from './state.service';
import { TenantService } from './tenant.service';
import { UserGroupService } from './user-group.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class AccessLevelService {
  private state = inject(StateService);
  private userService = inject(UserService);
  private tenantService = inject(TenantService);
  private galleryMemberService = inject(GalleryMemberService);
  private galleryPasswordService = inject(GalleryPasswordService);
  private loader = inject(DataLoaderService);
  private observableLoader = inject(ObservableDataLoaderService);
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private userGroupService = inject(UserGroupService);
  private permissionService = inject(PermissionService);

  public async getAccessLevel(
    gallery: GalleryDto,
    options: { ignoreMemberList?: boolean; ignorePublicAccessLevel?: boolean } = {}
  ): Promise<GalleryAccessLevel> {
    await firstValueFrom(this.userService.getUser());
    const galleryTenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);

    const [tenantAccessLevel, _publicAccessLevel] = await this.getAccessLevels(gallery);
    if (!this.state.user) return _publicAccessLevel; // handling not logged in users
    let GalleryGroupAccessLevel: GalleryAccessLevel = 'no_access';

    const publicAccessLevel = options.ignorePublicAccessLevel ? 'no_access' : _publicAccessLevel;

    if (!galleryTenantId) {
      console.warn('Gallery doesnt contain a tenant ID, falling back to tenant access level', gallery);
      return this.returnHighest([tenantAccessLevel, publicAccessLevel]);
    }

    // Checking if you are part of the team
    const isPartOfTeam = this.state.tenants.find(t => t.id === galleryTenantId);

    // If this user is the creator of the gallery, they should always have edit access
    if (this.state.user.id === ((gallery.created_by as DisplayUser)?.id || (gallery.created_by as string)))
      return 'edit';

    if (isPartOfTeam) {
      // Making sure that the galleries tenant is the active one
      if (this.state.activeTenant?.id !== galleryTenantId) {
        await this.tenantService.setActiveTenant(isPartOfTeam);
      }

      // If the user is an admin or the owner, they always have edit access
      if (this.state.activeTenantRole === 'admin' || this.state.activeTenantRole === 'owner') {
        return 'edit';
      }

      GalleryGroupAccessLevel = await this.getMaxAccessLevelForGalleryUserGroupsUsers(gallery);
    }

    // Shortcut to avoid requesting member list when not needed
    if ((tenantAccessLevel === 'edit' || GalleryGroupAccessLevel === 'edit') && isPartOfTeam) {
      return this.returnHighest([tenantAccessLevel, GalleryGroupAccessLevel]);
    }

    if (!options.ignoreMemberList) {
      try {
        const gm = await firstValueFrom(this.getGalleryMembers(gallery, !!isPartOfTeam));
        const member =
          gm.galleryMembers.find(m => m.user['id'] === this.state.user.id) ||
          gm.parentGalleryMembers.find(m => m.user['id'] === this.state.user.id);
        if (member) {
          if (isPartOfTeam) {
            return this.returnHighest([publicAccessLevel, member.access, tenantAccessLevel, GalleryGroupAccessLevel]);
          } else {
            return this.returnHighest([publicAccessLevel, member.access]);
          }
        }
      } catch (err) {
        if (!(err instanceof HttpErrorResponse)) throw err;
        if (err.status !== 403 && err.status !== 401) throw err;
      }
    }

    // Fallback for public access
    if (!isPartOfTeam) return this.returnHighest([publicAccessLevel]);

    // Fallback for team members
    return this.returnHighest([tenantAccessLevel, publicAccessLevel, GalleryGroupAccessLevel]);
  }

  private getGalleryFromCache(galleryId: string) {
    return forkJoinSafe(
      this.observableLoader.getFromCacheFuzzy<GalleryDto[]>('/galleries?').map(resp => resp.observable.pipe(take(1)))
    ).pipe(
      map(galleryLists => galleryLists.flat().filter(Boolean)),
      map(galleries => {
        return galleries.find(g => g.id === galleryId);
      })
    );
  }

  private async getParentAccessLevels(
    gallery: GalleryDto
  ): Promise<[GalleryTenantAccessLevel, GalleryPublicAccessLevel]> {
    return this.loader.get('galleries_access-levels_' + gallery.parent_gallery_id, async () => {
      try {
        const parentGallery = await firstValueFrom(this.getGalleryFromCache(gallery.parent_gallery_id));
        if (parentGallery) return [parentGallery.tenant_access, parentGallery.public_access];

        const data: GalleryDto = await this.loader.get('galleries_with-parent-access_' + gallery.id, async () => {
          return lastValueFrom(
            this.http.get<GalleryDto>(
              `${this.config.apiEndpoint}/galleries/${
                gallery.id
              }?include[]=parent_public_access&include[]=parent_tenant_access${this.getPassword(
                gallery.id
              )}&ignore_401_error=true`
            )
          );
        });
        return [data.parent_tenant_access, data.parent_public_access];
      } catch {
        return [gallery.tenant_access || 'edit', gallery.public_access || 'view_only'];
      }
    });
  }

  public async getAccessLevels(gallery: GalleryDto): Promise<[GalleryTenantAccessLevel, GalleryPublicAccessLevel]> {
    if (!gallery.section) return [gallery.tenant_access, gallery.public_access];
    if (gallery.tenant_access && gallery.public_access) return [gallery.tenant_access, gallery.public_access];
    const [parentTenantAccessLevel, parentPublicAccessLevel] = await this.getParentAccessLevels(gallery);
    return [gallery.tenant_access ?? parentTenantAccessLevel, gallery.public_access ?? parentPublicAccessLevel];
  }

  private getGalleryMembers(gallery: GalleryDto, isPartOfTeam: boolean) {
    if (!gallery.section) {
      return this.galleryMemberService.getGalleryMembers(gallery.id, isPartOfTeam).pipe(
        map(members => {
          return {
            galleryMembers: members,
            parentGalleryMembers: [],
          };
        })
      );
    }
    return forkJoin([
      this.galleryMemberService.getGalleryMembers(gallery.id, isPartOfTeam).pipe(first()),
      this.galleryMemberService.getGalleryMembers(gallery['parent_gallery_id'], isPartOfTeam).pipe(first()),
    ]).pipe(map(([galleryMembers, parentGalleryMembers]) => ({ galleryMembers, parentGalleryMembers })));
  }

  private async getMaxAccessLevelForGalleryUserGroupsUsers(gallery: GalleryDto): Promise<GalleryAccessLevel> {
    if (!this.permissionService.canDisplayElement(billingScopes.groups.list)) return 'no_access';
    const galleryId = gallery.section ? gallery['parent_gallery_id'] : gallery.id;
    const groups = await this.userGroupService.getGalleryUserGroups(galleryId);
    const accessLevels = await Promise.all(
      groups.map(async group => {
        const users = await this.userGroupService.getUserGroupUsers(group.group);
        if (users.includes(this.state.user.id)) return group.access;
      })
    );
    return this.returnHighest(accessLevels);
  }

  public isGalleryGuest(gallery: GalleryDto): Observable<boolean> {
    if (!this.state.user) return of(false);

    const galleryTenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
    if (this.state.tenants?.find(t => t.id === galleryTenantId)) return of(false);

    return this.getGalleryMembers(gallery, false).pipe(
      map(gm => {
        const isGalleryMember = !![...gm.galleryMembers, ...gm.parentGalleryMembers].find(
          m => m.user['id'] === this.state.user.id
        );
        return isGalleryMember;
      }),
      catchError(() => of(false))
    );
  }

  getMemberActions(myRole: string, memberRole: string, memberCount: number, memberIsCurrentUser: boolean) {
    const actions: string[] = [];
    if (this.canRemove(myRole, memberRole, memberIsCurrentUser)) actions.push('remove');
    if (this.canLeave(memberRole, memberIsCurrentUser)) actions.push('leave');
    if (this.canChangeRole(myRole, memberRole)) actions.push('changeRole');
    if (this.canTransferOwnership(myRole, memberRole, memberCount)) actions.push('transferOwnership');
    return actions;
  }

  canChangeRole(myRole: string, memberRole: string) {
    return ['admin', 'owner'].includes(myRole) && memberRole !== 'owner';
  }

  canTransferOwnership(myRole: string, memberRole: string, memberCount: number) {
    if (memberCount === 1) return false;
    return myRole === 'owner' && memberRole === 'owner';
  }

  canRemove(myRole: string, memberRole: string, memberIsCurrentUser: boolean) {
    if (memberRole === 'owner') return false;
    if (!['admin', 'owner'].includes(myRole)) return false;
    return !memberIsCurrentUser;
  }

  canLeave(memberRole: string, memberIsCurrentUser: boolean) {
    if (memberRole === 'owner') return false;
    if (memberRole === 'member') return false;
    return memberIsCurrentUser;
  }

  private returnHighest(accessLevels: GalleryAccessLevel[]): GalleryAccessLevel {
    accessLevels.sort((a, b) => (accessLevelOrder.indexOf(a) < accessLevelOrder.indexOf(b) ? 1 : -1));
    return accessLevels[0];
  }

  private getPassword(galleryId: string) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return '&password=' + encodeURIComponent(password);
  }
}
